/** Typographic minus (U+2212) for displayed negatives. */
export const MINUS = '\u2212';

/**
 * Formats a number into a currency string (e.g., $1,234.567).
 * Now accepts custom decimal precision (defaults to 2).
 *
 * @param value The number to format.
 * @param decimals Number of decimal places (default 2).
 * @param currency The currency symbol to use (defaults to 'USD').
 * @param locale The locale for formatting rules (defaults to 'en-US').
 * @returns A formatted currency string.
 */
export const formatCurrency = (
  value: number | null | undefined,
  decimals: number = 2,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  const num = value ?? 0;
  if (!Number.isFinite(num)) return '—';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
    .format(num)
    .replace(/^-/, MINUS);
};


/**
 * Formats a number into a percentage string (e.g., 5.25%).
 * The input value should be a decimal (e.g., 0.0525 for 5.25%).
 * Handles null or undefined inputs gracefully.
 *
 * @param value The decimal number to format as a percentage.
 * @param locale The locale for formatting rules (defaults to 'en-US').
 * @returns A formatted percentage string, or a fallback string like '0.00%'.
 */
export const formatPercent = (
  value: number | null | undefined,
  locale: string = 'en-US'
): string => {
  const num = value ?? 0;         // Usa 0 si es null/undefined
  if (!Number.isFinite(num)) return '—';
  const absNum = num;   // Formatea siempre el valor absoluto

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absNum);
};


/**
 * A more compact number formatter for cases where space is limited.
 * E.g., 12345 -> 12.3k, 1234567 -> 1.2M
 *
 * @param value The number to format.
 * @param locale The locale for formatting rules (defaults to 'en-US').
 * @returns A compact, formatted number string.
 */
export const formatCompactNumber = (
    value: number | null | undefined,
    locale: string = 'en-US'
): string => {
    const num = value || 0;

    return new Intl.NumberFormat(locale, {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1,
    }).format(num);
}

/**
 * Formatea un número con separadores de miles y una cantidad fija de decimales.
 * @param value Número o string convertible a número.
 * @param decimals Número de decimales deseados (por defecto 2).
 * @param locale The locale for formatting rules (defaults to 'en-US').
 * @returns String con separadores de miles, o '-' si no es convertible.
 */
export function formatNumber(
  value: number | string | null | undefined, 
  decimals = 2,
  locale: string = 'en-US'
): string {
  if (value === null || value === undefined) {
    return '-';
  }
  // Convertir a número
  const num = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
  if (isNaN(num)) {
    return '-';
  }
  return num.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats a wallet address to a shortened version (e.g., 0x1234...5678).
 * @param address The full wallet address.
 * @param startLength The number of characters to show at the beginning.
 * @param endLength The number of characters to show at the end.
 * @returns The formatted wallet address string.
 */
export const formatWalletAddress = (address: string | null | undefined, startLength = 6, endLength = 4): string => {
  if (!address) {
    return '-';
  }
  if (address.length < startLength + endLength) {
    return address;
  }
  return `${address.substring(0, startLength)}…${address.substring(address.length - endLength)}`;
};

/**
 * Percent with an explicit sign for deltas (e.g. +6.31%, -1.98%).
 */
/** Sign of a value as displayed: anything that rounds to zero at `decimals` is unsigned ("0.00%"). */
const displayedSign = (num: number, decimals: number): number => {
  const rounded = Number(num.toFixed(decimals));
  return rounded > 0 ? 1 : rounded < 0 ? -1 : 0;
};

export const formatSignedPercent = (value: number | null | undefined): string => {
  const num = value ?? 0;
  if (!Number.isFinite(num)) return "—";
  const formatted = formatPercent(Math.abs(num));
  const sign = displayedSign(num * 100, 2);
  if (sign > 0) return `+${formatted}`;
  if (sign < 0) return `${MINUS}${formatted}`;
  return formatPercent(0);
};

/**
 * Currency with an explicit sign for deltas (e.g. +$1,234.56, -$80.00).
 */
export const formatSignedCurrency = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  const num = value ?? 0;
  if (!Number.isFinite(num)) return "—";
  const formatted = formatCurrency(Math.abs(num), decimals);
  const sign = displayedSign(num, decimals);
  if (sign > 0) return `+${formatted}`;
  if (sign < 0) return `${MINUS}${formatted}`;
  return formatCurrency(0, decimals);
};

/**
 * Compact currency for dense stat cells (e.g. $972.4K, $1.24M).
 */
export const formatCompactCurrency = (value: number | null | undefined): string => {
  const num = value ?? 0;
  if (num === 0) return formatCurrency(0, 0);
  if (Math.abs(num) < 1000) return formatCurrency(num, 2);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    minimumSignificantDigits: 3,
    maximumSignificantDigits: 3,
  })
    .format(num)
    .replace(/^-/, MINUS);
};

/** Difference between two returns, in percentage points: "+3.40 pp" (input is a fraction). */
export const formatSignedPp = (value: number | null | undefined): string => {
  const num = (value ?? 0) * 100;
  if (!Number.isFinite(num)) return "—";
  const abs = Math.abs(num).toFixed(2);
  const sign = displayedSign(num, 2);
  if (sign > 0) return `+${abs} pp`;
  if (sign < 0) return `${MINUS}${abs} pp`;
  return "0.00 pp";
};

/**
 * Signed USD for summary cards: whole dollars from $1k up, cents below
 * (ledgers and tables keep cents via formatSignedCurrency).
 */
/** Card figures drop cents from $100 (ledgers keep them via formatCurrency). */
export const USD_CENTS_BELOW = 100;

/** A USD amount rounded the way cards display it (whole dollars from $100, cents below). */
export const roundUsdForDisplay = (value: number): number =>
  Math.abs(value) >= USD_CENTS_BELOW ? Math.round(value) : Math.round(value * 100) / 100;

export const formatSignedUsd = (value: number | null | undefined): string => {
  const num = value ?? 0;
  return formatSignedCurrency(num, Math.abs(num) >= USD_CENTS_BELOW ? 0 : 2);
};

/** Price / token quantity: up to 4 decimals (2 from 1,000 up), no trailing zeros. */
export const formatQuantity = (value: number | null | undefined, maxDecimals?: number): string => {
  const num = value ?? 0;
  if (!Number.isFinite(num)) return "—";
  const decimals = maxDecimals ?? (Math.abs(num) >= 1000 ? 2 : 4);
  return num
    .toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    .replace(/^-/, MINUS);
};

const USD_STABLES = new Set(["USDC", "USDC.E", "USDBC", "USDT", "USDT0", "RUSDT", "DAI", "DOC", "USDRIF", "FDUSD", "BUSD", "TUSD", "PYUSD", "USDE", "GHO", "LUSD", "FRAX"]);

export const isUsdStable = (symbol?: string | null): boolean => !!symbol && USD_STABLES.has(symbol.toUpperCase());

/**
 * Unit of a pair price: USD when either side is a USD stablecoin (the price is quoted in it),
 * otherwise token1 — FET/WETH reads "0.000231 WETH", not "$0.000231".
 */
export const pairQuote = (token0?: string | null, token1?: string | null): string | null =>
  isUsdStable(token0) || isUsdStable(token1) ? null : token1 || null;

/**
 * Token quantity for display: no decimals from 1,000, 2 decimals from 1, 4 significant digits
 * below 1 ("64,122 FET", "12.35 WETH", "0.004269 ETH"). Full precision belongs in a title/tooltip.
 */
export const formatTokenAmount = (value: number | null | undefined): string => {
  const num = value ?? 0;
  if (!Number.isFinite(num)) return "—";
  const abs = Math.abs(num);
  const out =
    abs >= 1000
      ? num.toLocaleString("en-US", { maximumFractionDigits: 0 })
      : abs >= 1
      ? num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : num === 0
      ? "0"
      : num.toLocaleString("en-US", { maximumSignificantDigits: 4 });
  return out.replace(/^-/, MINUS);
};

/** What a pair's price measures: "WETH in USD" for stable pairs, "FET in WETH" otherwise. */
export const pairPriceLabel = (token0?: string | null, token1?: string | null): string => {
  if (isUsdStable(token0)) return `${token1 || "token"} in USD`;
  if (isUsdStable(token1)) return `${token0 || "token"} in USD`;
  return `${token0 || "token"} in ${token1 || "token"}`;
};

/** USD card figure: cents only under $100 (ledgers keep cents through formatCurrency). */
export const formatUsd = (value: number | null | undefined): string => {
  const num = value ?? 0;
  return formatCurrency(num, Math.abs(num) >= USD_CENTS_BELOW ? 0 : 2);
};

/** formatUsd, with "—" for zero / missing (empty slots in cards and tables). */
export const formatUsdOrDash = (value: number | null | undefined): string => (value ? formatUsd(value) : "—");

const BTC_QUOTES = new Set(["WRBTC", "RBTC", "WBTC", "BTC"]);

/**
 * Pair price with its unit, at a steady precision so Now / Floor / Ceiling line up:
 * "$2,648.31" for stable pairs, "0.0002310 WETH" (4 significant digits) otherwise, and BTC-quoted
 * prices in sats ("153.94 sats") instead of a run of zeros.
 */
export const formatPairPrice = (
  value: number | null | undefined,
  token0?: string | null,
  token1?: string | null,
  decimals = 2,
  /** Significant digits for sub-unit, non-USD prices (tooltips comparing two close prices use 6). */
  significant = 4
): string => {
  const quote = pairQuote(token0, token1);
  const v = value ?? 0;
  if (!quote) return formatCurrency(v, decimals);
  if (BTC_QUOTES.has(quote.toUpperCase()) && Math.abs(v) < 0.01) {
    return `${(v * 1e8).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} sats`;
  }
  if (Math.abs(v) < 1 && v !== 0) {
    return `${v.toLocaleString("en-US", {
      minimumSignificantDigits: significant,
      maximumSignificantDigits: significant,
    })} ${quote}`;
  }
  return `${formatQuantity(v, decimals)} ${quote}`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad2 = (n: number) => String(n).padStart(2, "0");

/** Absolute timestamp, one format app-wide: "Sep 23, 2026 · 18:58" (local time). */
export const formatDateTime = (ts: number | null | undefined): string => {
  if (!ts || !Number.isFinite(ts)) return "—";
  const d = new Date(ts);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** Date only: "Sep 23, 2026". */
export const formatDate = (ts: number | null | undefined): string => {
  if (!ts || !Number.isFinite(ts)) return "—";
  const d = new Date(ts);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

/** Chart axis month: "Dec '25". */
export const formatAxisMonth = (ts: number): string => {
  const d = new Date(ts);
  return `${MONTHS[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
};
